/**
 * Epic 278 Slice F — hazard-category rollups from loaded register rows.
 * Uses only v1 hazard-class keys already resolved from source workflow data.
 */

import type { HazardClassKey } from '../../pages/acceptanceProof/hazardClassReportOptions';
import { isHazardClassKey, V1_HAZARD_CLASS_OPTIONS } from '../../pages/acceptanceProof/hazardClassReportOptions';
import type { CaseRegisterRow, ScanRegisterRow } from './workspaceRegisters';
import { hazardCategoryCoverageCopy } from './safetyCasesWorkspacePresentationCopy';

export type HazardCategoryRollupBucket = {
  key: HazardClassKey;
  label: string;
  activeSafetyCases: number;
  totalCases: number;
  scansCompleted: number;
  scansBlockedByCoverage: number;
  awaitingSignOff: number;
  casesWithNewActivity: number;
  latestMeaningfulChange: string | null;
};

export type HazardCategoryRollup = {
  buckets: HazardCategoryRollupBucket[];
  classifiedCases: number;
  unclassifiedCases: number;
  classifiedActive: number;
  unclassifiedActive: number;
};

export function hazardCategoryFilterParam(): string {
  return 'hazard_category';
}

export function resolveHazardCategoryFilter(value: string | null | undefined): HazardClassKey | null {
  const normalized = String(value || '').trim().toLowerCase();
  return isHazardClassKey(normalized) ? normalized : null;
}

function timestamp(value?: string | null): number {
  const ms = new Date(String(value || '')).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function latestTimestampLabel(values: Array<string | null | undefined>): string | null {
  return values
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .sort((left, right) => timestamp(right) - timestamp(left))[0] || null;
}

function hasNewActivity(row: CaseRegisterRow): boolean {
  const label = String(row.newActivity || '').trim().toLowerCase();
  return Boolean(label) && label !== 'no' && label !== '—';
}

function scanBlockedByCoverage(row: ScanRegisterRow): boolean {
  return row.workflowState === 'Insufficient coverage'
    || String(row.coverage || '').trim().toLowerCase() === 'insufficient coverage';
}

export function buildHazardCategoryRollup(
  caseRows: CaseRegisterRow[],
  scanRows: ScanRegisterRow[],
): HazardCategoryRollup {
  const casesByCategory = new Map<HazardClassKey, CaseRegisterRow[]>();
  let unclassifiedCases = 0;

  for (const row of caseRows) {
    const key = row.hazardClassKey || null;
    if (!key) {
      unclassifiedCases += 1;
      continue;
    }
    const bucket = casesByCategory.get(key) || [];
    bucket.push(row);
    casesByCategory.set(key, bucket);
  }

  const caseCategoryByKey = new Map<string, HazardClassKey>();
  caseRows.forEach((row) => {
    if (row.hazardClassKey) caseCategoryByKey.set(row.id, row.hazardClassKey);
  });

  const buckets = V1_HAZARD_CLASS_OPTIONS.flatMap((option): HazardCategoryRollupBucket[] => {
    const rows = casesByCategory.get(option.key) || [];
    if (rows.length === 0) return [];

    const scans = scanRows.filter((scan) => caseCategoryByKey.get(scan.caseKey) === option.key);
    return [{
      key: option.key,
      label: option.displayName,
      activeSafetyCases: rows.filter((row) => row.controlStage !== 'Closed').length,
      totalCases: rows.length,
      scansCompleted: scans.filter((scan) => scan.workflowState === 'Signed').length,
      scansBlockedByCoverage: scans.filter(scanBlockedByCoverage).length,
      awaitingSignOff: scans.filter((scan) => scan.workflowState === 'Awaiting sign-off').length,
      casesWithNewActivity: rows.filter(hasNewActivity).length,
      latestMeaningfulChange: latestTimestampLabel([
        ...rows.map((row) => row.updatedAt),
        ...scans.map((scan) => scan.completedAt),
      ]),
    }];
  });

  const classifiedCases = caseRows.length - unclassifiedCases;
  const classifiedActive = buckets.reduce((sum, bucket) => sum + bucket.activeSafetyCases, 0);

  return {
    buckets,
    classifiedCases,
    unclassifiedCases,
    classifiedActive,
    unclassifiedActive: caseRows.filter((row) => !row.hazardClassKey && row.controlStage !== 'Closed').length,
  };
}

export function filterCasesByHazardCategory(
  rows: CaseRegisterRow[],
  hazardCategory: HazardClassKey | null,
): CaseRegisterRow[] {
  if (!hazardCategory) return rows;
  return rows.filter((row) => row.hazardClassKey === hazardCategory);
}

export function formatHazardCategoryCoverageCopy(
  rollup: Pick<HazardCategoryRollup, 'classifiedActive' | 'unclassifiedActive'>,
  totalActive?: number,
): string | null {
  const total = typeof totalActive === 'number'
    ? totalActive
    : rollup.classifiedActive + rollup.unclassifiedActive;
  if (total <= 0 || rollup.classifiedActive >= total) return null;
  return hazardCategoryCoverageCopy(rollup.classifiedActive, total);
}
